import {
  KeyValue,
  AddArrayControlAction,
  RemoveArrayControlAction,
  Unboxed,
  SetValueAction,
  FormGroupState,
  SetAsyncErrorAction,
  ClearAsyncErrorAction,
  StartAsyncValidationAction,
  unbox
} from 'ngrx-forms';
import { DxFormGroup } from './form-group';
import {
  AbstractForm,
  matchRelativeControlPath,
  toControlPath
} from './abstract-form';
import { IDxForm } from './form';
import {
  Injectable,
  OnInit
} from '@angular/core';
import {
  filter,
  map,
  distinctUntilChanged,
  tap,
  takeUntil,
  first,
  withLatestFrom
} from 'rxjs/operators';
import { FormRegisterBuilder } from './form-register-builder';
import { DxFormGroupDefinition } from './decorators/interfaces';
import {
  Observable,
  of,
  merge,
  EMPTY,
  Subject
} from 'rxjs';
import { ValidatorResult } from './decorators/validator';
import { validationResultRoValidationError } from './helpers';
import { Action } from '@ngrx/store';
import { DxSetValueAction } from './forms.actions';

@Injectable()
export class DxFormArray<
  FormValue extends KeyValue,
  GroupFormValue extends KeyValue = null,
  RootGroupFormValue extends KeyValue = GroupFormValue
> extends AbstractForm<FormValue[], GroupFormValue, RootGroupFormValue>
  implements OnInit {
  public get __result(): Array<Partial<Unboxed<FormValue>>> {
    return (this.__controls || []).map(control => control.__result);
  }

  public __initial = [];

  public __groupInitial!: FormValue;

  public __controls: Array<DxFormGroup<FormValue, FormValue[]>> = undefined;

  public __groupDefinition!: DxFormGroupDefinition<any>;

  /**
   * @internal
   */
  public __groupInstance: DxFormGroup<FormValue>;

  forEachControl(
    callback: (controlId: string, control: IDxForm<any, any>) => any
  ): void {
    if (this.__controls) {
      this.__controls.forEach((control, i) => callback(i.toFixed(0), control));
    }
  }

  public processAsyncValidators(
    action: SetValueAction<any>
  ): Observable<
    SetAsyncErrorAction | ClearAsyncErrorAction | StartAsyncValidationAction
    > {

    if (!action.controlId.includes(this.__controlId)) {
      return EMPTY;
    }

    this.logger.trace(
      `[${this.__uuid}::DxFormArray::processAsyncValidators::${
        this.__controlId
      }] call`,
      action
    );

    const groupControlId = action.controlId.replace(this.__controlId, '');

    const match = groupControlId.match(/\.(\d+)/);

    if (match) {

      const indexMatch = match[ 1 ];

      if (indexMatch) {
        const index = Number(indexMatch[ 0 ]);

        const control = this.__controls[ index ];

        if (
          control.hasOwnProperty('_groupedAsyncValidators') &&
          Object.keys(control[ '_groupedAsyncValidators' ]).length
        ) {
          return control[ 'processAsyncValidators' ](action);
        }

      }

    }

    return EMPTY;
  }

  public processOnSetValue(
    action: SetValueAction<any>
  ): Observable<void | Action> {

    if (!action.controlId.includes(this.__controlId)) {
      return EMPTY;
    }

    this.logger.trace(
      `[${this.__uuid}::DxFormArray::processOnSetValue::${
        this.__controlId
      }] call`,
      action
    );

    const groupControlId = action.controlId.replace(this.__controlId, '');

    const match = groupControlId.match(/\.(\d+)/);

    if (match) {

      const indexMatch = match[ 1 ];

      if (indexMatch) {
        const index = Number(indexMatch[ 0 ]);

        const control = this.__controls[ index ];

        if (
          control &&
          control.hasOwnProperty('_groupedOnSetValue') &&
          Object.keys(control[ '_groupedOnSetValue' ]).length
        ) {
          return control[ 'processOnSetValue' ](action);
        }

      }

    }

    return EMPTY;
  }

  public init(): void {
    super.init();

    const builder = new FormRegisterBuilder(
      (<any>this).constructor,
      this.logger
    );

    this.processAsyncValidatorsDecorator(builder);
    this.processOnSetValueDecorator(builder);

    this.__groupInstance = this.buildFormGroup(this.__groupDefinition);
    this.__groupInstance.init();
    this.__groupInitial = this.__groupInstance.__initial;
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.logger.trace(`[DxFormArray::ngOnInit::${this.__controlId}] call`);
    this.__value$ = this.__parent.__value$.pipe(
      filter(Boolean),
      map(value => value[this.__controlName]),
      distinctUntilChanged((a, b) => this.isEqual(a, b)),
      filter(Boolean),
      tap(value =>
        this.logger.trace(
          `[DxFormArray::ngOnInit::${this.__controlId}] new value`,
          value
        )
      ),
      takeUntil(this.__onDestroy$)
    );
    this.__state$ = this.__parent.__state$.pipe(
      filter(Boolean),
      map((state: FormGroupState<any>) =>
        Array.isArray(state.controls)
          ? state.controls[+this.__controlName]
          : (state.controls[this.__controlName] as any)
      ),
      filter(Boolean),
      takeUntil(this.__onDestroy$)
    );
    this._subscription.add(
      this.__state$.subscribe(state => (this.__state = state))
    );
    this._subscription.add(
      this.__value$.subscribe(value => (this.__value = value))
    );

    // Update controls if value changes
    this._subscription.add(
      this.__value$
        .pipe(
          filter(Boolean),
          filter(value => Array.isArray(value) && value.length !== 0),
          filter(value => this.__controls === undefined),
          first(),
        )
        .subscribe(currentValue => this.buildFormGroups(currentValue))
    );
    this._subscription.add(
      this.__onReset$.subscribe(() => this.clearControls())
    );
    this.__onInit$.next();
  }

  public buildFormGroups(currentValue: FormValue[]): void {
    this.logger.trace(
      `[DxFormArray::buildFormGroups::${this.__controlId}] call`,
      currentValue
    );
    const controls = [];

    for (let i = 0; i < currentValue.length; i++) {
      const group = this.buildFormGroup(this.__groupDefinition, i.toFixed(0));
      group.init();
      group.ngOnInit();
      controls.push(group);
    }

    this.logger.trace(
      `[DxFormArray::buildFormGroups::${this.__controlId}] new controls`,
      controls
    );

    this.__controls = controls;

    this.forEachControl((_, control) => (control.__initialized = true));
  }

  public addFormGroup(initial: Partial<Unboxed<FormValue>> = {}): void {
    this.logger.trace(`[DxFormArray::addFormGroup::${this.__controlId}] call`);
    if (!this.__controls) {
      this.__controls = [];
    }
    this.store$.dispatch(
      new AddArrayControlAction(this.__controlId, {
        ...this.__groupInitial,
        ...this.__groupInstance.box(initial)
      })
    );
    const group: DxFormGroup<any, any, any> = this.buildFormGroup(
      this.__groupDefinition,
      this.__controls.length.toFixed(0)
    );
    group.init();
    // manuel set initialized. Normally after the formDefinition is successfully
    // initialized all controls are marked as initialized. This group is create
    // after the normal initialize phase. Therefore a manuel set is required!!
    // Otherwise some functionality is not available
    // (example: dispatching of SetUserDefinedPropertyAction Actions on Label, Disabled, ... set)
    group.__initialized = true;
    group.ngOnInit();
    this.__controls.push(group);
  }

  public removeFormGroup(index: number): void {
    this.logger.trace(
      `[DxFormArray::removeFormGroup::${this.__controlId}] call`
    );
    this.store$.dispatch(new RemoveArrayControlAction(this.__controlId, index));
    const group: DxFormGroup<any, any, any> = this.__controls.splice(
      index,
      1
    )[0];
    if (group) {
      group.ngOnDestroy();
    } else {
      this.logger.warn(`[DxFormArray::removeFormGroup::${this.__controlId}] group with index '${index}' is not defined`, { group, controls: this.__controls });
    }
    for (let i = index; i < this.__controls.length; i++) {
      this.__controls[i].setControlId(i.toFixed(0), this.__controlId);
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.clearControls();
  }

  private clearControls(): void {
    if (this.__controls) {
      this.__controls.forEach(control => control.ngOnDestroy());
      this.__controls = undefined;
    }
  }

  public moveControl(previousIndex: number, currentIndex: number): void {
    if (previousIndex != currentIndex) {
      const currentValue = this.__value[currentIndex];
      const previousValue = this.__value[previousIndex];
      this.__controls[currentIndex].setValue(previousValue);
      this.__controls[previousIndex].setValue(currentValue);
    }
  }

  public getControlByControlId(controlId: string): IDxForm<any, any> {
    const index = +controlId;
    if (this.__controls.length > index) {
      return this.__controls[index];
    } else {
      throw new Error(
        `[DxFormArray::getControlByControlId::${
          this.__controlId
        }] control with index '${index}' not found`
      );
    }
  }

  public box(value: Partial<Unboxed<FormValue[]>>): FormValue[] {
    return (value || [] as any).map(v => this.__groupInstance.box(v));
  }

  public setValue(value: Array<Unboxed<FormValue>>): void {
    this.store$.dispatch(new DxSetValueAction(this.__controlId, value, `FormArray(${this.constructor.name})::setValue`));
  }
}
